import React, { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, ArrowRight, MapPin, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SiteHeader } from '../components/home/SiteHeader';
import { SiteFooter } from '../components/home/SiteFooter';
import { Breadcrumb } from '../components/Breadcrumb';
import { CrossSellCTA, EstimateCTA } from '../components/CrossSellCTA';
import { FinancingCTA } from '../components/FinancingCTA';
import { trackClick } from '../utils/analytics';
import { serviceHubs } from '../data/hubPagesData';

const ServiceHub = () => {
  const { hubSlug } = useParams();
  const routerLocation = useLocation();
  const slug = hubSlug || routerLocation.pathname.replace(/^\//, '');
  const hub = serviceHubs[slug];

  useEffect(() => {
    if (hub) document.title = hub.metaTitle;
  }, [hub]);

  if (!hub) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-zinc-900 mb-4">Page Not Found</h1>
          <Link to="/" className="text-red-600 hover:text-red-700 font-semibold">Back to Home</Link>
        </div>
      </div>
    );
  }

  const Icon = hub.icon;
  const faqSchema = hub.faqs?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": hub.faqs.map(f => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": hub.title,
    "provider": {
      "@type": "LocalBusiness",
      "name": "North Texas Welding and Fabrication, LLC",
      "telephone": "817-723-5006",
      "address": { "@type": "PostalAddress", "addressLocality": "Springtown", "addressRegion": "TX" },
      "areaServed": hub.serviceAreas?.map(city => ({ "@type": "City", "name": city + ", TX" }))
    },
    "areaServed": hub.serviceAreas?.map(city => ({ "@type": "City", "name": city + ", TX" }))
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`hub-${slug}`}>
      <Helmet>
        <meta name="description" content={hub.metaDesc} />
        <link rel="canonical" href={`https://ntxpipefence.com/${hub.slug}`} />
        <meta property="og:title" content={hub.metaTitle} />
        <meta property="og:description" content={hub.metaDesc} />
        <meta property="og:url" content={`https://ntxpipefence.com/${hub.slug}`} />
      </Helmet>

      <SiteHeader mobileMenuOpen={false} setMobileMenuOpen={() => {}} />

      <div className="pt-24">
        <Breadcrumb items={[{ label: hub.title }]} />
      </div>

      {/* Hero */}
      <section className="bg-zinc-900 py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzI2MjYyNiIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {hub.brandLine && <p className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-2">{hub.brandLine}</p>}
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">{hub.h1}</h1>
              <p className="text-zinc-400 text-lg mb-6">{hub.subtitle}</p>
              <p className="text-zinc-300 leading-relaxed mb-8">{hub.intro}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/instant-estimate"
                  onClick={() => trackClick('Instant Estimate - Hub Hero', `/${hub.slug}`)}
                  className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-lg px-8 py-4 rounded-lg transition-colors"
                  data-testid="hub-hero-estimate-cta"
                >
                  Get an Instant Estimate <ArrowRight size={18} />
                </Link>
                <a href="tel:8177235006" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-black font-semibold text-lg px-8 py-4 rounded-lg transition-colors">
                  <Phone size={18} /> 817-723-5006
                </a>
              </div>
            </div>
            <div className="relative">
              <img src={hub.heroImage} alt={hub.h1} className="rounded-xl shadow-2xl w-full object-cover aspect-[4/3]" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Points */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-zinc-900 mb-10 text-center">What We Offer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hub.keyPoints.map((point) => (
              <div key={point} className="flex items-start gap-3 p-4 bg-zinc-50 rounded-lg">
                <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="text-white" size={14} />
                </div>
                <p className="text-zinc-700 font-medium text-sm">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sub-Services */}
      {hub.subServices?.length > 0 && (
        <section className="py-16 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-3 text-center">{hub.title} Services</h2>
            <p className="text-zinc-600 text-center mb-10 max-w-2xl mx-auto">Explore our specialized {hub.title.toLowerCase()} offerings</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hub.subServices.map((sub) => (
                <Link key={sub.href + sub.label} to={sub.href} className="group" onClick={() => trackClick(`SubService: ${sub.label}`, `/${hub.slug}`)}>
                  <Card className="border-zinc-200 hover:border-red-600 transition-all hover:shadow-lg h-full">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-zinc-900 mb-2 group-hover:text-red-600 transition-colors">{sub.label}</h3>
                      <p className="text-zinc-600 text-sm mb-3">{sub.desc}</p>
                      <span className="text-red-600 text-sm font-semibold flex items-center gap-1">View Details <ArrowRight size={14} /></span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Estimate CTA */}
      <EstimateCTA
        heading={`Get a ${hub.title} Estimate`}
        subtext={`See preliminary pricing for your ${hub.title.toLowerCase()} project in minutes. No obligation, no pressure.`}
        pagePath={`/${hub.slug}`}
      />

      {/* Materials */}
      {hub.materials && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-3 text-center">{hub.materials.title}</h2>
            <p className="text-zinc-600 text-center mb-10 max-w-2xl mx-auto">{hub.materials.description}</p>
            <div className="grid md:grid-cols-2 gap-6">
              {hub.materials.items.map((item) => (
                <div key={item.name} className="bg-zinc-50 rounded-lg p-6 border border-zinc-200">
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{item.name}</h3>
                  <p className="text-zinc-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Factors */}
      {hub.pricingFactors && (
        <section className="py-16 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-8 text-center">{hub.pricingFactors.title}</h2>
            <div className="bg-white rounded-xl p-8 border border-zinc-200 shadow-sm">
              <ul className="space-y-3 mb-6">
                {hub.pricingFactors.factors.map((factor) => (
                  <li key={factor} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 shrink-0"></div>
                    <span className="text-zinc-700">{factor}</span>
                  </li>
                ))}
              </ul>
              {hub.pricingFactors.note && (
                <p className="text-sm text-zinc-500 border-t border-zinc-200 pt-4 italic">{hub.pricingFactors.note}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Installation Process */}
      {hub.installProcess && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-10 text-center">{hub.installProcess.title}</h2>
            <div className="space-y-6">
              {hub.installProcess.steps.map((s) => (
                <div key={s.step} className="flex gap-4">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">{s.step}</div>
                  <div className="flex-1 pb-4 border-b border-zinc-100">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1">
                      <h3 className="font-bold text-zinc-900">{s.title}</h3>
                      <span className="text-xs text-red-600 font-semibold">{s.timeline}</span>
                    </div>
                    <p className="text-zinc-600 text-sm">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-zinc-500 text-sm mt-8 font-medium">{hub.installProcess.totalTimeline}</p>
          </div>
        </section>
      )}

      {/* Permitting */}
      {hub.permitting && (
        <section className="py-12 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-zinc-900 mb-4 text-center">Permits & Regulations</h2>
            <p className="text-zinc-600 leading-relaxed text-center max-w-3xl mx-auto">{hub.permitting}</p>
          </div>
        </section>
      )}

      {/* FAQs */}
      {hub.faqs?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-10 text-center">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {hub.faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-zinc-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-zinc-900 hover:text-red-600 py-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-600 leading-relaxed pb-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Cross-Sell */}
      {hub.crossSell?.length > 0 && (
        <CrossSellCTA
          items={hub.crossSell}
          title="Related Services"
          subtitle="North Texas Welding and Fabrication handles multiple stages of your property project under one contractor."
          pagePath={`/${hub.slug}`}
        />
      )}

      {/* Service Areas + Local Pages */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-zinc-900 mb-3 text-center">{hub.title} Service Areas</h2>
          <p className="text-zinc-600 text-center mb-10">Serving communities across North Texas</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 max-w-4xl mx-auto mb-10">
            {hub.serviceAreas?.map((area) => (
              <div key={area} className="bg-white p-4 rounded-lg text-center border border-zinc-200 hover:border-red-600 transition-all">
                <MapPin size={16} className="mx-auto mb-1 text-red-600" />
                <p className="font-semibold text-zinc-900 text-sm">{area}, TX</p>
              </div>
            ))}
          </div>
          {hub.localPages?.length > 0 && (
            <div className="text-center">
              <p className="text-zinc-500 text-sm mb-3 font-medium uppercase tracking-wider">Detailed Local Service Pages</p>
              <div className="flex flex-wrap justify-center gap-3">
                {hub.localPages.map((lp) => (
                  <Link key={lp.href} to={lp.href} className="text-red-600 hover:text-red-700 text-sm font-semibold underline underline-offset-2">
                    {hub.title} in {lp.city}, TX
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <FinancingCTA sourcePage={`/${hub.slug}`} />

      <SiteFooter />
    </div>
  );
};

export default ServiceHub;
